/**
 * Invoice Form State Hook
 * 
 * Centralized state management for invoice form data.
 * Provides initial state, update functions, and reset capabilities.
 * 
 * IMPORTANT: Form state initialization is driven by BACKEND CONFIG.
 * invoiceConfig parameter comes from backend API response merged with UI metadata.
 * 
 * FEATURE #7: Frontend is a pure renderer. Backend owns all business logic:
 * - Invoice numbers are generated by backend on finalization
 * - Totals/tax are calculated by backend
 * - Validation is performed by backend
 */

import { useState, useCallback, useMemo, useEffect, useRef } from 'react';
import { getTodayDate, getDueDateFromToday } from '../utils/helpers';
import { getCurrencySymbol } from '../utils/currencyFormatter';
import { createEmptyLineItem } from '../utils/calculations';
import { DEFAULT_TERMS_TEXT, DEFAULT_PAYMENT_TERMS_DAYS } from '../utils/constants';

// ============================================================================
// INITIAL STATE FACTORY
// ============================================================================

/**
 * Create initial invoice form state
 * 
 * State is derived from backend config (SINGLE SOURCE OF TRUTH):
 * - Currency from config.currency.default
 * - Tax rate from config.tax.defaultRate
 * - GST fields enabled from config.fields.gstin
 * - Bank fields enabled from config.fields.showBankDetails
 * 
 * @param {Object} options - Configuration options
 * @param {Object} options.taxRates - Tax rates by country
 * @param {Object} options.invoiceConfig - Merged config (backend + UI)
 * @returns {Object} Initial form state
 */
function createInitialFormState({ taxRates = {}, invoiceConfig = null } = {}) {
  // Backend config drives default values
  const currency = invoiceConfig?.currency?.default || 'USD';
  const defaultCountry = currency === 'INR' ? 'India' : 'United States';
  
  // Tax rate from backend config, fallback to country tax rate
  const defaultTaxRate = invoiceConfig?.tax?.defaultRate ?? taxRates[defaultCountry] ?? 0;
  
  const baseState = {
    // Business Information
    business_name: '',
    business_email: '',
    business_phone: '',
    business_address: '',
    business_city: '',
    business_state: '',
    business_zip: '',
    business_country: defaultCountry,
    business_logo: null,
    
    // Client Information
    client_name: '',
    client_email: '',
    client_phone: '',
    client_address: '',
    client_city: '',
    client_state: '',
    client_zip: '',
    client_country: defaultCountry,
    
    // Invoice Details
    // FEATURE #7: No invoice_number - backend generates on finalization
    invoice_date: getTodayDate(),
    due_date: getDueDateFromToday(DEFAULT_PAYMENT_TERMS_DAYS),
    po_number: '',
    
    // Line Items (start with one empty item)
    items: [createEmptyLineItem()],
    
    // Calculated Totals (managed by backend via useCalculations hook)
    subtotal: 0,
    tax_rate: defaultTaxRate, // From backend config
    tax_amount: 0,
    discount: 0,
    total: 0,
    
    // Additional Fields
    notes: '',
    terms: invoiceConfig?.labels?.termsPlaceholder || DEFAULT_TERMS_TEXT,
    
    // Currency Settings - from backend config
    currency: currency,
    currency_symbol: getCurrencySymbol(currency),
    
    // Invoice Type
    invoice_type: invoiceConfig?.id || 'freelancer',
  };
  
  // Add GST-specific fields if enabled by backend config
  if (invoiceConfig?.fields?.gstin) {
    baseState.business_gstin = '';
    baseState.client_gstin = '';
    baseState.place_of_supply = '';
    baseState.is_same_state = true;
    // GST rates: CGST + SGST = total GST rate
    const gstRate = invoiceConfig?.tax?.defaultRate || 18;
    baseState.cgst_rate = gstRate / 2;
    baseState.sgst_rate = gstRate / 2;
    baseState.igst_rate = gstRate;
  }
  
  // Add bank details if enabled by backend config
  if (invoiceConfig?.fields?.showBankDetails) {
    baseState.bank_name = '';
    baseState.bank_account = '';
    baseState.bank_swift = '';
    baseState.bank_iban = '';
  }
  
  return baseState;
}

// ============================================================================
// MAIN HOOK
// ============================================================================

/**
 * Custom hook for managing invoice form state
 * 
 * IMPORTANT: invoiceConfig comes from backend API (single source of truth)
 * merged with frontend UI metadata.
 * 
 * @param {Object} initialTaxRates - Tax rates by country from API
 * @param {Object} initialCurrencies - Currencies from API (not currently used)
 * @param {Object} invoiceConfig - Merged config from backend + UI metadata
 * @returns {Object} Form state and update functions
 * 
 * @example
 * const { formData, updateFormData, resetForm } = useInvoiceForm(taxRates, currencies, invoiceConfig);
 */
export function useInvoiceForm(initialTaxRates = {}, initialCurrencies = {}, invoiceConfig = null) {
  // Create initial state with backend-driven config
  const initialState = useMemo(
    () => createInitialFormState({ taxRates: initialTaxRates, invoiceConfig }),
    [invoiceConfig, initialTaxRates] // Recreate when config changes
  );
  
  const [formData, setFormData] = useState(initialState);

  // When invoiceConfig first becomes available (null → config), ensure the form has
  // all type-specific fields. This handles the async config load race condition:
  // useState() only captures initialState once at mount, but invoiceConfig arrives later.
  const configLoadedRef = useRef(false);
  useEffect(() => {
    if (!invoiceConfig || configLoadedRef.current) return;
    configLoadedRef.current = true;

    setFormData(prev => {
      const hasUserInput = Boolean(prev.business_name || prev.client_name || prev.business_email);

      // Fresh form — apply full type-specific defaults
      if (!hasUserInput) return initialState;

      // User already started typing — only inject fields that are MISSING.
      // Critical for GST: if the user typed a business name before config loaded,
      // the GST fields (place_of_supply, cgst_rate, etc.) were never added.
      const patches = {};

      if (invoiceConfig?.fields?.gstin && !('place_of_supply' in prev)) {
        const gstRate = invoiceConfig?.tax?.defaultRate || 18;
        Object.assign(patches, {
          place_of_supply: '',
          is_same_state: true,
          business_gstin: prev.business_gstin ?? '',
          client_gstin: prev.client_gstin ?? '',
          tax_rate: gstRate,
          cgst_rate: gstRate / 2,
          sgst_rate: gstRate / 2,
          igst_rate: gstRate,
        });
      }

      if (invoiceConfig?.fields?.showBankDetails && !('bank_name' in prev)) {
        Object.assign(patches, {
          bank_name: '',
          bank_account: '',
          bank_swift: '',
          bank_iban: '',
        });
      }

      // Apply locked currency from backend config if not yet set
      if (invoiceConfig?.currency?.default && !prev.currency) {
        patches.currency = invoiceConfig.currency.default;
        patches.currency_symbol = getCurrencySymbol(invoiceConfig.currency.default);
      }

      return Object.keys(patches).length > 0 ? { ...prev, ...patches } : prev;
    });
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [invoiceConfig]);

  /**
   * Update form data with partial updates
   * Merges updates with existing state
   * @param {Object} updates - Fields to update
   */
  const updateFormData = useCallback((updates) => {
    if (!updates || typeof updates !== 'object') return;
    
    setFormData(prev => {
      // Create new state by merging updates
      const newState = { ...prev, ...updates };
      
      // Auto-update currency symbol when currency changes
      if (updates.currency && updates.currency !== prev.currency) {
        newState.currency_symbol = getCurrencySymbol(updates.currency);
      }
      
      return newState;
    });
  }, []);
  
  /**
   * Update a specific line item
   * FEATURE #7: Does not compute amount - backend calculates all totals
   * @param {number} index - Index of the item to update
   * @param {string} field - Field name to update
   * @param {any} value - New value
   */
  const updateLineItem = useCallback((index, field, value) => {
    setFormData(prev => {
      if (index < 0 || index >= prev.items.length) return prev;
      
      const newItems = [...prev.items];
      newItems[index] = {
        ...newItems[index],
        [field]: value,
      };
      
      // FEATURE #7: Remove local amount calculation
      // Backend will calculate amounts via /api/invoices/calculate
      
      return { ...prev, items: newItems };
    });
  }, []);
  
  /**
   * Add a new empty line item
   */
  const addLineItem = useCallback(() => {
    setFormData(prev => ({
      ...prev,
      items: [...prev.items, createEmptyLineItem()],
    }));
  }, []);
  
  /**
   * Remove a line item by index
   * @param {number} index - Index of the item to remove
   */
  const removeLineItem = useCallback((index) => {
    setFormData(prev => {
      // Don't remove if it's the last item
      if (prev.items.length <= 1) return prev;
      
      return {
        ...prev,
        items: prev.items.filter((_, i) => i !== index),
      };
    });
  }, []);
  
  /**
   * Reset form to initial state with fresh invoice number and dates
   */
  const resetForm = useCallback(() => {
    setFormData(prev => ({
      ...createInitialFormState({ taxRates: initialTaxRates, invoiceConfig }),
      // Preserve settings that user might want to keep
      currency: prev.currency,
      currency_symbol: prev.currency_symbol,
      terms: prev.terms,
    }));
  }, [initialTaxRates, invoiceConfig]);
  
  /**
   * Reset only invoice-specific fields (keep business info)
   */
  const resetInvoice = useCallback(() => {
    setFormData(prev => ({
      ...prev,
      // Reset client info
      client_name: '',
      client_email: '',
      client_phone: '',
      client_address: '',
      client_city: '',
      client_state: '',
      client_zip: '',
      client_country: 'United States',
      
      // Reset invoice details (no invoice_number - backend generates)
      invoice_date: getTodayDate(),
      due_date: getDueDateFromToday(DEFAULT_PAYMENT_TERMS_DAYS),
      po_number: '',
      
      // Reset items and totals
      items: [createEmptyLineItem()],
      subtotal: 0,
      tax_amount: 0,
      discount: 0,
      total: 0,
      notes: '',
    }));
  }, []);
  
  return {
    formData,
    setFormData,
    updateFormData,
    updateLineItem,
    addLineItem,
    removeLineItem,
    resetForm,
    resetInvoice,
  };
}

export default useInvoiceForm;